import { noop } from 'lodash';
import { useMemo } from 'react';

import { useTrackSwitchedSortType } from '@air/analytics';
import { CheckIcon } from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { useGetSubnavSortValue, useSubnavSort } from '~/swr-hooks/subnav/useSubnavSort';
import { useSubnavSortOptions } from '~/swr-hooks/subnav/useSubnavSortOptions';
import { useUpdateSubnavSort } from '~/swr-hooks/subnav/useUpdateSubnavSort';

type UseSortMenuOptionsParams = {
  libraryId?: string;
  location: string;
};

export const useSortMenuOptions = ({ libraryId, location }: UseSortMenuOptionsParams) => {
  const { data: sortOptionsData } = useSubnavSortOptions();
  const { data: currentSubnavSort } = useSubnavSort();
  const { getSubNavSortValue } = useGetSubnavSortValue();
  const { mutate: updateSubnavSort } = useUpdateSubnavSort();
  const { trackSwitchedSortType } = useTrackSwitchedSortType();

  const sortDropdownMenuOptions = useMemo(() => {
    if (libraryId) {
      const currentSubnavSortForLibrary = getSubNavSortValue(libraryId);
      const currentLibrarySort = currentSubnavSortForLibrary?.libraryBoardSorts?.[0];

      const options: DropdownMenuOption[] =
        sortOptionsData?.map(({ displayName, name, direction }) => {
          const isActiveOption = name === currentLibrarySort?.name && direction === currentLibrarySort?.direction;

          return {
            id: `${name}-${direction}`,
            label: displayName,
            onSelect: !isActiveOption
              ? () => {
                  updateSubnavSort({ name, direction, libraryId });
                  trackSwitchedSortType({ sortingFieldName: name, direction, location });
                }
              : noop,
            suffix: isActiveOption ? <CheckIcon size="sm" /> : null,
            type: 'item',
          };
        }) ?? [];

      return options;
    }

    const currentBoardSort = currentSubnavSort?.boardSort;

    const options: DropdownMenuOption[] =
      sortOptionsData?.map(({ displayName, name, direction }) => {
        const isActiveOption = name === currentBoardSort?.name && direction === currentBoardSort?.direction;

        return {
          id: `${name}-${direction}`,
          label: displayName,
          onSelect: !isActiveOption
            ? () => {
                updateSubnavSort({ name, direction });
                trackSwitchedSortType({ sortingFieldName: name, direction, location });
              }
            : noop,
          suffix: isActiveOption ? <CheckIcon size="sm" /> : null,
          type: 'item',
        };
      }) ?? [];

    return options;
  }, [
    currentSubnavSort,
    getSubNavSortValue,
    sortOptionsData,
    libraryId,
    updateSubnavSort,
    trackSwitchedSortType,
    location,
  ]);

  return { sortDropdownMenuOptions };
};
